import type {
	ServiceBusStatistics,
	Queue,
	Topic,
	Subscription,
	DeadLetterMessage,
	ServiceBusMessage,
	SubQueue,
	StreamMessagesOptions,
	StreamMessagesHandlers,
	DeadLetterLocator,
} from '../types/management-servicebus.types';

/**
 * Toggle this to `false` once the intermediate `/management/service-bus/...`
 * endpoints are live. Every route method already has the real API call wired up
 * behind this flag, so swapping from mock to live data is a one-line change.
 *
 * While `true`, the fixtures below define the *proposed* request/response
 * contract the intermediate service should implement.
 */
export const USE_MOCK_DATA = false;

/** Default page size for message streaming when the caller doesn't specify one. */
const DEFAULT_PAGE_SIZE = 50;

/** Simulate network latency so loading states are exercised against mock data. */
export const mockDelay = <T>(value: T, ms = 350): Promise<T> => {
	return new Promise((resolve) => {
		return setTimeout(() => {
			return resolve(value);
		}, ms);
	});
};

// ---------------------------------------------------------------------------
// Mock fixtures — proposed contract for the intermediate management service.
// ---------------------------------------------------------------------------

export const MOCK_STATISTICS: ServiceBusStatistics = {
	entities: {
		queues: 3,
		topics: 2,
		subscriptions: 5,
	},
	messages: {
		total: 1_431,
		active: 1_388,
		deadLetter: 27,
		scheduled: 12,
		transfer: 0,
		transferDeadLetter: 0,
	},
	sizeInBytes: 4_404_019,
};

export const MOCK_QUEUES: Queue[] = [
	{
		name: 'review-ingest',
		activeMessageCount: 842,
		scheduledMessageCount: 9,
		deadLetterMessageCount: 14,
		totalMessageCount: 865,
		sizeInBytes: 3_276_800,
	},
	{
		name: 'place-details-sync',
		activeMessageCount: 311,
		scheduledMessageCount: 3,
		deadLetterMessageCount: 5,
		totalMessageCount: 319,
		sizeInBytes: 1_048_576,
	},
	{
		name: 'aggregation-rebuild',
		activeMessageCount: 95,
		scheduledMessageCount: 0,
		deadLetterMessageCount: 0,
		totalMessageCount: 95,
		sizeInBytes: 262_144,
	},
];

export const MOCK_TOPICS: Topic[] = [
	{
		name: 'company-events',
		subscriptionCount: 3,
		scheduledMessageCount: 8,
		sizeInBytes: 2_310_144,
	},
	{
		name: 'channel-events',
		subscriptionCount: 2,
		scheduledMessageCount: 4,
		sizeInBytes: 1_048_576,
	},
];

export const MOCK_SUBSCRIPTIONS: Record<string, Subscription[]> = {
	'company-events': [
		{
			subscriptionName: 'search-indexer',
			totalMessageCount: 126,
			activeMessageCount: 120,
			deadLetterMessageCount: 6,
			transferMessageCount: 0,
			transferDeadLetterMessageCount: 0,
			createdAt: '2026-04-02T09:14:22Z',
			modifiedAt: '2026-06-10T16:48:05Z',
			accessedAt: '2026-06-15T07:31:40Z',
		},
		{
			subscriptionName: 'audit-log',
			totalMessageCount: 18,
			activeMessageCount: 18,
			deadLetterMessageCount: 0,
			transferMessageCount: 0,
			transferDeadLetterMessageCount: 0,
			createdAt: '2026-04-02T09:14:22Z',
			modifiedAt: '2026-05-28T11:02:13Z',
			accessedAt: '2026-06-15T06:55:18Z',
		},
		{
			subscriptionName: 'webhook-dispatch',
			totalMessageCount: 6,
			activeMessageCount: 4,
			deadLetterMessageCount: 2,
			transferMessageCount: 0,
			transferDeadLetterMessageCount: 0,
			createdAt: '2026-04-02T09:14:22Z',
			modifiedAt: '2026-06-12T20:41:09Z',
			accessedAt: '2026-06-15T07:12:54Z',
		},
	],
	'channel-events': [
		{
			subscriptionName: 'search-indexer',
			totalMessageCount: 64,
			activeMessageCount: 64,
			deadLetterMessageCount: 0,
			transferMessageCount: 0,
			transferDeadLetterMessageCount: 0,
			createdAt: '2026-04-18T13:27:51Z',
			modifiedAt: '2026-06-09T08:19:44Z',
			accessedAt: '2026-06-15T05:48:30Z',
		},
		{
			subscriptionName: 'webhook-dispatch',
			totalMessageCount: 9,
			activeMessageCount: 9,
			deadLetterMessageCount: 0,
			transferMessageCount: 0,
			transferDeadLetterMessageCount: 0,
			createdAt: '2026-04-18T13:27:51Z',
			modifiedAt: '2026-06-11T22:03:36Z',
			accessedAt: '2026-06-14T19:30:02Z',
		},
	],
};

const sampleMessages = (prefix: string, count: number): DeadLetterMessage[] => {
	return Array.from({ length: count }, (_, i) => {
		return {
			sequenceNumber: Number(`${prefix.length}${1000 + i}`),
			messageId: `${prefix}-msg-${1000 + i}`,
			enqueuedAt: `2026-06-1${(i % 2) + 1}T0${(i % 8) + 1}:24:0${i % 6}Z`,
			deadLetterReason: i % 2 ? 'MaxDeliveryCountExceeded' : 'ProcessingError',
			deadLetterErrorDescription:
				i % 2
					? 'Message could not be consumed after the maximum number of delivery attempts.'
					: 'Handler threw while processing the message payload.',
			body: `{"entity":"${prefix}","index":${i},"payload":"…"}`,
		};
	});
};

/** DLQ fixtures keyed by a stable locator string (see `locatorKey`). */
export const MOCK_DEAD_LETTER: Record<string, DeadLetterMessage[]> = {
	'queue:review-ingest': sampleMessages('review-ingest', 14),
	'queue:place-details-sync': sampleMessages('place-details-sync', 5),
	'queue:aggregation-rebuild': [],
	'sub:company-events/search-indexer': sampleMessages('search-indexer', 6),
	'sub:company-events/webhook-dispatch': sampleMessages('webhook-dispatch', 2),
};

export const locatorKey = (locator: DeadLetterLocator): string => {
	return locator.kind === 'queue'
		? `queue:${locator.queueName}`
		: `sub:${locator.topicName}/${locator.subscriptionName}`;
};

/** The entity identifiers the batch action responses echo back. */
export const locatorIds = (
	locator: DeadLetterLocator,
): { queueName: string } | { topicName: string; subscriptionName: string } => {
	return locator.kind === 'queue'
		? { queueName: locator.queueName }
		: { topicName: locator.topicName, subscriptionName: locator.subscriptionName };
};

// ---------------------------------------------------------------------------
// Streamed message fixtures — proposed SSE contract for queue/topic messages.
// ---------------------------------------------------------------------------

const sampleStreamMessages = (
	prefix: string,
	count: number,
	subQueue: SubQueue,
): ServiceBusMessage[] => {
	return Array.from({ length: count }, (_, i) => {
		return {
			sequenceNumber: `${prefix.length}${1000 + i}`,
			messageId: `${prefix}-msg-${1000 + i}`,
			body: `{"entity":"${prefix}","index":${i},"payload":"…"}`,
			deliveryCount: subQueue === 'deadLetter' ? 10 : (i % 3) + 1,
			enqueuedTimeUtc: `2026-06-1${(i % 2) + 1}T0${(i % 8) + 1}:24:0${i % 6}Z`,
			...(subQueue === 'deadLetter'
				? {
						deadLetterReason: i % 2 ? 'MaxDeliveryCountExceeded' : 'ProcessingError',
						deadLetterErrorDescription:
							i % 2
								? 'Message could not be consumed after the maximum number of delivery attempts.'
								: 'Handler threw while processing the message payload.',
					}
				: {}),
		};
	});
};

/** Streamed-message fixtures keyed by `${locatorKey}:${subQueue}`. */
const MOCK_MESSAGES: Record<string, ServiceBusMessage[]> = {
	'queue:review-ingest:main': sampleStreamMessages('review-ingest', 23, 'main'),
	'queue:review-ingest:deadLetter': sampleStreamMessages('review-ingest', 14, 'deadLetter'),
	'queue:place-details-sync:main': sampleStreamMessages('place-details-sync', 12, 'main'),
	'queue:place-details-sync:deadLetter': sampleStreamMessages(
		'place-details-sync',
		5,
		'deadLetter',
	),
	'queue:aggregation-rebuild:main': sampleStreamMessages('aggregation-rebuild', 7, 'main'),
	'queue:aggregation-rebuild:deadLetter': [],
	'sub:company-events/search-indexer:main': sampleStreamMessages('search-indexer', 10, 'main'),
	'sub:company-events/search-indexer:deadLetter': sampleStreamMessages(
		'search-indexer',
		6,
		'deadLetter',
	),
	'sub:company-events/webhook-dispatch:main': sampleStreamMessages('webhook-dispatch', 4, 'main'),
	'sub:company-events/webhook-dispatch:deadLetter': sampleStreamMessages(
		'webhook-dispatch',
		2,
		'deadLetter',
	),
};

/**
 * Emulate the SSE stream against the mock fixtures: dispatch one `message` per
 * row in the requested page, then a terminal `page-end`. Cursor pagination is
 * honoured by resuming after the `fromSequenceNumber` row.
 */
export const emulateMessageStream = async (
	locator: DeadLetterLocator,
	subQueue: SubQueue,
	options: StreamMessagesOptions,
	handlers: StreamMessagesHandlers,
): Promise<void> => {
	const all = MOCK_MESSAGES[`${locatorKey(locator)}:${subQueue}`] ?? [];
	const limit = options.limit ?? DEFAULT_PAGE_SIZE;

	let startIndex = 0;
	if (options.fromSequenceNumber) {
		const cursorIndex = all.findIndex((m) => {
			return m.sequenceNumber === options.fromSequenceNumber;
		});
		startIndex = cursorIndex >= 0 ? cursorIndex + 1 : 0;
	}

	const page = all.slice(startIndex, startIndex + limit);

	await mockDelay(null, 250);

	for (const message of page) {
		handlers.onMessage(message);
	}

	const consumed = startIndex + page.length;
	const hasMore = consumed < all.length;
	handlers.onPageEnd({
		hasMore,
		nextSequenceNumber:
			hasMore && page.length ? page[page.length - 1].sequenceNumber : undefined,
	});
};
